/**
 * Desktop Multi-Column Analytical Dashboard Layout (>= 1024px)
 * Left column (65%): Analytical Stats, Interactive Trend Chart, Filter Controls & Daily Record Feed.
 * Right column (35%): Stationary Clinical Data Entry Form, AHA Guideline Reference & Clinical Tips.
 */

import React from 'react';
import {
  BloodPressureRecord,
  DailyRecordGroup as DailyRecordGroupType,
  BPStatistics,
  DateRangePreset,
  RecordFormData,
} from '@/types/record';
import { getAllCategories } from '@/utils/bpClassification';
import { TrendChart } from './TrendChart';
import { FilterBar } from './FilterBar';
import { StatsSummary } from './StatsSummary';
import { DailyRecordGroup } from '@/components/records/DailyRecordGroup';
import { RecordForm } from '@/components/records/RecordForm';
import { StampBadge } from '@/components/common/StampBadge';
import { Card } from '@/components/common/Card';

export interface DesktopLayoutProps {
  records: BloodPressureRecord[];
  allRecords: BloodPressureRecord[];
  dailyGroups: DailyRecordGroupType[];
  stats: BPStatistics;
  activePreset: DateRangePreset;
  startDate?: string;
  endDate?: string;
  searchQuery?: string;
  onPresetChange: (preset: DateRangePreset) => void;
  onDateRangeChange: (start: string, end: string) => void;
  onSearchChange: (query: string) => void;
  onResetFilter: () => void;
  onExportCSV: () => void;
  onPrint?: () => void;
  onCreateRecord: (data: RecordFormData) => Promise<void> | void;
  onEditRecord: (record: BloodPressureRecord) => void;
  onDeleteRecord: (id: number) => void;
  onAddRecordForDate?: (date: string) => void;
  isLoading?: boolean;
  className?: string;
}

export const DesktopLayout: React.FC<DesktopLayoutProps> = ({
  records,
  allRecords,
  dailyGroups,
  stats,
  activePreset,
  startDate,
  endDate,
  searchQuery,
  onPresetChange,
  onDateRangeChange,
  onSearchChange,
  onResetFilter,
  onExportCSV,
  onPrint,
  onCreateRecord,
  onEditRecord,
  onDeleteRecord,
  onAddRecordForDate,
  isLoading = false,
  className = '',
}) => {
  const categories = getAllCategories();

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-6 items-start ${className}`}>
      {/* ========================================================================= */}
      {/* Left / Main Analytical Stream (lg:col-span-8 - 66.6% width)                */}
      {/* ========================================================================= */}
      <div className="lg:col-span-8 space-y-6">
        {/* Top Analytical Stats Cards */}
        {records.length > 0 && (
          <StatsSummary stats={stats} records={records} />
        )}

        {/* Time-series Trend Visualization Chart */}
        <TrendChart records={records} height={320} />

        {/* Filter, Preset & CSV Export Bar */}
        <FilterBar
          activePreset={activePreset}
          startDate={startDate}
          endDate={endDate}
          searchQuery={searchQuery}
          totalFilteredCount={records.length}
          totalCount={allRecords.length}
          onPresetChange={onPresetChange}
          onDateRangeChange={onDateRangeChange}
          onSearchChange={onSearchChange}
          onReset={onResetFilter}
          onExportCSV={onExportCSV}
          onPrint={onPrint}
        />

        {/* Daily Record Ledger Stream (Chronological Feed) */}
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-2 border-b-2 border-ink-900">
            <h3 className="font-serif font-bold text-sm sm:text-base uppercase tracking-wider text-ink-900">
              臨床量測病歷帳冊 (Clinical Ledger Stream)
            </h3>
            <span className="font-serif text-xs text-ink-500">
              共 <strong>{dailyGroups.length}</strong> 個記錄日 • <strong>{records.length}</strong> 筆量測
            </span>
          </div>

          {dailyGroups.length === 0 ? (
            <div className="bg-paper-100 border border-dashed border-paper-400 rounded-xs p-8 text-center space-y-2">
              <div className="text-3xl text-ink-400">📋</div>
              <h4 className="font-serif font-bold text-sm text-ink-800">
                此篩選區間內尚無血壓紀錄
              </h4>
              <p className="font-serif text-xs text-ink-500 max-w-sm mx-auto">
                您可點擊右側表單登記第一筆血壓數據，或點擊「重設篩選」查閱全部歷史。
              </p>
              <button
                type="button"
                onClick={onResetFilter}
                className="mt-2 inline-block px-3 py-1 bg-paper-200 hover:bg-paper-300 border border-paper-400 rounded-xs text-xs font-serif font-bold text-ink-900"
              >
                重設篩選條件
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {dailyGroups.map((group, index) => (
                <DailyRecordGroup
                  key={group.date}
                  date={group.date}
                  formattedDateZh={group.formattedDateZh}
                  records={group.records}
                  summary={group.summary}
                  onEditRecord={onEditRecord}
                  onDeleteRecord={onDeleteRecord}
                  onAddRecordForDate={onAddRecordForDate}
                  defaultExpanded={index === 0 || index === 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* Right Sidebar: Stationary Entry Form & Clinical Legend (lg:col-span-4 - 33.3%) */}
      {/* ========================================================================= */}
      <div className="lg:col-span-4 space-y-6">
        {/* Stationary Clinical Entry Form */}
        <div className="sticky top-4 space-y-6">
          <RecordForm
            onSubmit={onCreateRecord}
            isLoading={isLoading}
            title="臨床數據即時登錄"
          />

          {/* AHA Clinical Guidelines Reference Card */}
          <Card
            title="AHA / ACC 血壓診斷指引"
            subtitle="CLINICAL REFERENCE STANDARDS"
            variant="default"
          >
            <div className="space-y-2.5 text-xs font-serif">
              {categories.map((cat) => (
                <div
                  key={cat.category}
                  className="p-2 bg-paper-200/80 border border-paper-300 rounded-xs space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <StampBadge category={cat.category} size="sm" />
                    <span className="font-mono text-[11px] text-ink-700 font-bold">
                      {cat.description}
                    </span>
                  </div>
                  <p className="text-[11px] text-ink-600 leading-tight pt-0.5">
                    {cat.recommendation}
                  </p>
                </div>
              ))}
            </div>
          </Card>

          {/* Doctor Consultation & Measurement Advice */}
          <Card
            title="正確量測指引提醒"
            subtitle="MEASUREMENT BEST PRACTICES"
            variant="parchment"
          >
            <ul className="space-y-1.5 text-xs font-serif text-ink-800 list-disc list-inside leading-relaxed">
              <li><strong>722 原則</strong>：連續 7 天、早晚各 2 次、每次間隔 1 分鐘取平均。</li>
              <li>量測前 30 分鐘請勿抽菸、飲用咖啡因或進行劇烈運動。</li>
              <li>量測時保持靜坐 5 分鐘，手臂平放與心臟同高，請勿說話。</li>
              <li>若出現<strong>高血壓危象 (&gt;180/120)</strong> 且伴隨胸悶、頭痛，請立即就醫！</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};
